interface CustomProps {
  get: (key: string) => string | undefined;
  set: (key: string, value: string) => void;
  saveAsync: (callback: (result: Office.AsyncResult<void>) => void) => void;
}

interface SaveBlockResponse {
  ID: string;
}

interface TimeBlockData {
  id: string | null;
  interview: boolean;
  feedback: boolean;
  note: string;
}

const API_BASE = "https://de3w46f2vogw1.cloudfront.net/v1";
const API_ENDPOINT = "api/analysts/outlook";
const API_KEY = "qkRlio9DYG6Otjwc8PALgqWSettHggEZ";

function setStatus(message: string, isError = false): void {
  const statusEl = document.getElementById("statusMessage") as HTMLElement;
  statusEl.style.color = isError ? "red" : "green";
  statusEl.textContent = message;
}

// Utility to show/hide a spinner inside a button
function setButtonLoading(buttonId: string, isLoading: boolean) {
  const btn = document.getElementById(buttonId) as HTMLButtonElement | null;
  const spinner = document.querySelector(`#${buttonId} .spinner`) as HTMLElement | null;

  if (!btn) return;

  if (isLoading) {
    btn.disabled = true;
    if (spinner) spinner.classList.remove("hidden");
  } else {
    btn.disabled = false;
    if (spinner) spinner.classList.add("hidden");
  }
}

// Utility to show/hide a spinner inside a button
function setButtonDisabled(buttonId: string, isLoading: boolean) {
  const btn = document.getElementById(buttonId) as HTMLButtonElement | null;

  if (!btn) return;

  if (isLoading) {
    btn.disabled = true;
  } else {
    btn.disabled = false;
  }
}

// Read checkbox states
function getBlockDataFromUI(): TimeBlockData {
  const interview = (document.getElementById("chkInterview") as HTMLInputElement).checked;
  const feedback = (document.getElementById("chkFeedback") as HTMLInputElement).checked;
  const note = (document.getElementById("note") as HTMLTextAreaElement).value.trim();
  const id = (document.getElementById("id") as HTMLInputElement).value;

  return { interview, feedback, note, id };
}

function getEventStart(): Promise<Date> {
  return new Promise((resolve, reject) => {
    Office.context.mailbox.item.start.getAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value as Date);
      } else {
        reject(result.error);
      }
    });
  });
}

function getEventEnd(): Promise<Date> {
  return new Promise((resolve, reject) => {
    Office.context.mailbox.item.end.getAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value as Date);
      } else {
        reject(result.error);
      }
    });
  });
}

// Set checkbox states and note from loaded data
function setUIFromBlockData(data: TimeBlockData) {
  (document.getElementById("chkInterview") as HTMLInputElement).checked = data.interview;
  (document.getElementById("chkFeedback") as HTMLInputElement).checked = data.feedback;
  (document.getElementById("note") as HTMLTextAreaElement).value = data.note || "";
  (document.getElementById("id") as HTMLInputElement).value = data.id || null;
}

function showConfirmDialog(message: string): Promise<boolean> {
  return new Promise((resolve) => {
    Office.context.ui.displayDialogAsync(
      `${window.location.origin}/confirm.html?msg=${encodeURIComponent(message)}`,
      { height: 30, width: 30, displayInIframe: true },
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
          const dialog = asyncResult.value;

          dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
            resolve(arg.message === "yes");
            dialog.close();
          });

          dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
            resolve(false);
          });
        } else {
          console.error("Dialog failed:", asyncResult.error);
          resolve(false);
        }
      }
    );
  });
}

async function saveTimeBlock() {
  setButtonLoading("saveBtn", true);
  setButtonDisabled("deleteBtn", true);
  setStatus("⏳ Saving...");

  try {
    const userEmail = Office.context.mailbox.userProfile.emailAddress;
    const blockData = getBlockDataFromUI();
    const start = await getEventStart();
    const end = await getEventEnd();
    console.log("Event start:", start.toUTCString());
    console.log("Event end:", end.toUTCString());

    // Optional: validate at least one checkbox is checked
    if (!blockData.interview && !blockData.feedback) {
      setStatus("Please select at least Interview or Feedback.", true);
      return;
    }

    const response = await fetch(`${API_BASE}/${API_ENDPOINT}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...blockData, email: userEmail, api_key: API_KEY, start, end})
    });

    if (!response.ok) throw new Error(`API save failed: ${response.statusText} ${await response.text()}`);

    const { ID }: SaveBlockResponse = await response.json();
    console.log("✅ Saved remotely with blockID:", ID);

    await Office.context.mailbox.item.loadCustomPropertiesAsync(
      (asyncResult: Office.AsyncResult<CustomProps>) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.error("Failed to load custom properties:", asyncResult.error);
          setStatus("Time block saved remotely, but failed to link blockID in Outlook.");
          return;
        }

        const customProps = asyncResult.value;
        customProps.set("blockID", ID);
        customProps.saveAsync((saveResult) => {
          if (saveResult.status === Office.AsyncResultStatus.Failed) {
            console.error("Failed to save custom properties:", saveResult.error);
            setStatus("Time block saved remotely, but failed to store link in Outlook.");
          } else {
            setStatus("✅ Time block saved in Outlook and remote service.");
          }
        });
      }
    );

    let bodyDescription = ""
    if (blockData.interview && blockData.feedback) {
      bodyDescription = "Interview & Feedback";
    } else if (blockData.interview) {
      bodyDescription = "Interview";
    } else if (blockData.feedback) {
      bodyDescription = "Feedback";
    }

    await Office.context.mailbox.item.subject.setAsync("Scheduling Availability - " + bodyDescription);
    await Office.context.mailbox.item.saveAsync((saveResult) => {
      console.log("saveResult:", saveResult);
      if (saveResult.status === Office.AsyncResultStatus.Failed) {
        console.error("Failed to save item:", saveResult.error);
        setStatus("❌ Failed to save time block.");
        return
      } else {
        setStatus("✅ Time block saved in Outlook and remote service.");
      }
    });

  } catch (err) {
    console.error(err);
    setStatus("❌ Failed to save time block.");
  } finally{
    setButtonLoading("saveBtn", false);
    setButtonDisabled("deleteBtn", false);
    if (Office.context.platform != Office.PlatformType.OfficeOnline) {
      Office.context.mailbox.item.close();
    }
    console.log("using in the web so not closing it automatically");
  }
}

async function deleteTimeBlock() {
  setButtonLoading("deleteBtn", true);
  setButtonDisabled("saveBtn", true);
  setStatus("⏳ Deleting...");

  try {
    const blockData = getBlockDataFromUI();
    const userEmail = Office.context.mailbox.userProfile.emailAddress;

    console.log("Block data:", blockData);
    Office.context.mailbox.item.loadCustomPropertiesAsync(
      async (asyncResult: Office.AsyncResult<CustomProps>) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.error("Failed to load custom properties:", asyncResult.error);
          setStatus("❌ Couldn't find linked time block info.");
          return;
        }

        const customProps = asyncResult.value;
        const id = customProps.get("blockID");

        if (!id) {
          setStatus("❌ No linked time block found for this event.");
          return;
        }

        const response = await fetch(`${API_BASE}/${API_ENDPOINT}`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id, email: userEmail, api_key: API_KEY })
        });

        if (!response.ok) throw new Error(`API delete failed: ${response.statusText}`);
        console.log("✅ Remote service delete successful");

        const itemId = Office.context.mailbox.item.itemId;
        if (!itemId) {
          setStatus("Remote delete succeeded. Please delete from Outlook manually.");
          return;
        }

        try {
          const accessToken = await OfficeRuntime.auth.getAccessToken({ allowSignInPrompt: true });
          const graphResponse = await fetch(`https://graph.microsoft.com/v1.0/me/events/${itemId}`, {
            method: "DELETE",
            headers: {
              "Authorization": `Bearer ${accessToken}`,
              "Accept": "application/json"
            }
          });

          if (graphResponse.ok) {
            setStatus("✅ Time block removed from Outlook and remote service.");
          } else {
            console.error("Graph delete failed", await graphResponse.text());
            setStatus("Remote delete succeeded, but couldn't remove from Outlook. Please delete manually.");
          }

        } catch (graphError) {
          console.error("Graph API error:", graphError);
          setStatus("Remote delete succeeded, but couldn't remove from Outlook. Please delete manually.");
        }
      }
    );

  } catch (err) {
    console.error(err);
    setStatus("❌ Failed to delete time block.");
  }finally{
    setButtonLoading("deleteBtn", false);
    setButtonDisabled("saveBtn", false);
  }
}

function loadExistingTimeBlock() {
  const blockData = getBlockDataFromUI();
  Office.context.mailbox.item.loadCustomPropertiesAsync((asyncResult) => {
    if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
      setStatus("Failed to load custom properties", true);
      return;
    }

    const props = asyncResult.value;
    const blockID = props.get("blockID");
    console.log("Props:", props.getAll());
    console.log("Block ID:", blockID);

    if (blockID) {
      fetch(`${API_BASE}/${API_ENDPOINT}/${blockID}`)
        .then(res => {
          if (!res.ok) throw new Error(`API returned ${res.status}`);
          return res.json();
        })
        .then((data: TimeBlockData) => {
          setUIFromBlockData(data);
          setStatus("Loaded existing time block");
        })
        .catch(err => {
          setStatus(`Failed to load time block: ${err.message}`, true);
        });
    }
  });
}

// Hook buttons
document.getElementById("saveBtn")?.addEventListener("click", (button) => {
  saveTimeBlock();
});

document.getElementById("deleteBtn")?.addEventListener("click", () => {
  deleteTimeBlock();
});

// Load existing block on startup
Office.onReady(() => {
  loadExistingTimeBlock();
});